import styled from 'styled-components/macro';
import { darken, lighten } from '../../utils';
import { SPACINGS } from '../../utils/style';

export const TooltipLabelWrapper = styled.div<{ accent: string }>`
  padding: ${SPACINGS[2]} ${SPACINGS[3]};
  background-color: ${(p) => lighten(p.accent, 0.9)};
  border-radius: 5px;
  box-shadow: ${(p) => p.theme.boxShadows.xs};
`;

export const TooltipRef = styled.div<{ percent: number }>`
  width: 1px;
  height: 1px;
  background-color: transparent;
  position: absolute;
  left: ${(p) => p.percent}%;
`;

export const InputRangeWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 55px;
  padding-bottom: 10px;

  & .input-container {
    display: flex;
    width: 100%;
    position: relative;
  }

  & .labels-container {
    width: 100%;
  }
`;

const thumbAdjustment = 13;
type InputRangeOffsetProps = {
  width: number;
  background: string;
};
export const InputRangeOffset = styled.div<InputRangeOffsetProps>`
  height: 6px;
  flex: 0 0 calc(${(p) => p.width}% - ${thumbAdjustment}px);
  background-color: ${(p) => p.background};
`;

export const InputLabelsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: ${SPACINGS[2]};
`;

type LabelBackgroundProps = {
  active: boolean;
  selected: boolean;
  accent: string;
  background: string;
};
export const LabelBackground = styled.div<LabelBackgroundProps>`
  height: 35px;
  width: 100%;
  transform: scaleY(${(p) => (p.active ? 1.35 : 1)});
  transform-origin: bottom;
  transition: background-color 0.2s, transform 0.2s;
  background-color: ${(p) =>
    p.selected ? lighten(p.accent, 0.9) : lighten(p.background, 0.95)};
`;

export const LabelWrapper = styled.div<{ active: boolean }>`
  position: absolute;
  top: 5px;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateY(${(p) => (p.active ? '-12px' : 0)});
  transition: transform 0.2s;
`;

type InputRangeStyledProps = {
  accent: string;
  background: string;
  activeBackground: string;
  width?: number;
};
export const InputRangeStyled = styled.input<InputRangeStyledProps>`
  margin: 0;
  flex: 0 0
    ${(p) =>
      p.width ? `calc(${p.width}% + ${thumbAdjustment * 2}px)` : '100%'};
  padding: 0;
  background-color: transparent;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  z-index: 1;

  &:focus {
    outline: none;
  }

  &:focus::-webkit-slider-thumb {
    background-color: ${(p) => darken(p.accent, 0.2)};
  }

  &:focus::-moz-range-thumb {
    background-color: ${(p) => darken(p.accent, 0.2)};
  }

  &:focus::-ms-thumb {
    background-color: ${(p) => darken(p.accent, 0.2)};
  }

  &::-moz-focus-outer {
    border: 0;
  }

  &::-webkit-slider-thumb {
    width: 20px;
    height: 20px;
    margin-top: -7px;
    background-color: ${(p) => p.accent};
    border: 0;
    border-radius: 10px;
    transition: background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out;
    appearance: none;
    box-shadow: ${(p) => p.theme.boxShadows.xs};
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background-color: ${(p) => p.accent};
    border: 0;
    border-radius: 10px;
    transition: background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out;
    appearance: none;
    box-shadow: ${(p) => p.theme.boxShadows.xs};
  }

  &::-ms-thumb {
    width: 20px;
    height: 20px;
    margin-top: 0;
    margin-right: 0.2rem;
    margin-left: 0.2rem;
    background-color: ${(p) => p.accent};
    border: 0;
    border-radius: 10px;
    transition: background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out;
    appearance: none;
    box-shadow: ${(p) => p.theme.boxShadows.xs};
  }

  @media (prefers-reduced-motion: reduce) {
    &::-webkit-slider-thumb {
      transition: none;
    }
  }

  &::-webkit-slider-thumb:active {
    background-color: ${(p) => darken(p.accent, 0.2)};
  }

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 6px;
    color: transparent;
    cursor: pointer;
    background-color: ${(p) => p.background};
    border-color: transparent;
    content: '';
  }

  @media (prefers-reduced-motion: reduce) {
    &::-moz-range-thumb {
      transition: none;
    }
  }

  &::-moz-range-thumb:active {
    background-color: ${(p) => darken(p.accent, 0.2)};
  }

  &::-moz-range-track {
    width: 100%;
    height: 6px;
    color: transparent;
    cursor: pointer;
    background-color: ${(p) => p.background};
    border-color: transparent;
  }

  &::-moz-range-progress {
    background-color: ${(p) => p.background};
    height: 6px;
  }

  @media (prefers-reduced-motion: reduce) {
    &::-ms-thumb {
      transition: none;
    }
  }

  &::-ms-thumb:active {
    background-color: ${(p) => darken(p.accent, 0.2)};
  }

  &::-ms-track {
    width: 100%;
    height: 6px;
    color: transparent;
    cursor: pointer;
    background-color: transparent;
    border-color: transparent;
    border-width: 0.5rem;
  }

  &::-ms-fill-lower {
    background-color: ${(p) => p.background};
    height: 6px;
  }

  &::-ms-fill-upper {
    margin-right: 15px;
    background-color: ${(p) => p.background};
  }

  &:disabled::-webkit-slider-thumb {
    background-color: ${(p) => p.background};
  }

  &:disabled::-webkit-slider-runnable-track {
    cursor: default;
  }

  &:disabled::-moz-range-thumb {
    background-color: ${(p) => p.background};
  }

  &:disabled::-moz-range-track {
    cursor: default;
  }

  &:disabled::-ms-thumb {
    background-color: ${(p) => p.background};
  }
`;

export const InputRangeTrack = styled.span<{ width: number }>`
  height: 6px;
  background-color: red;
  position: absolute;
  left: 0;
  width: ${(p) => p.width}%;
`;
